/* LX design layer: reveal-on-scroll, sliders with tabs, quick add, header state.
   Dependency-free, ~4KB. Loaded as a deferred module. */

window.lxReady = true;

const doc = document.documentElement;
const isRTL = () => doc.dir === 'rtl';

/* ---------------- Reveal on scroll ---------------- */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
      )
    : null;

function initReveal(scope = document) {
  scope.querySelectorAll('[data-lx-reveal]:not(.is-visible)').forEach((el) => {
    if (!revealObserver) return el.classList.add('is-visible');
    // Stagger direct reveal children automatically when no index is set
    el.querySelectorAll('.lx-r').forEach((child, i) => {
      if (!child.style.getPropertyValue('--i')) child.style.setProperty('--i', String(Math.min(i, 8)));
    });
    revealObserver.observe(el);
  });
}

/* ---------------- Slider (scroll-snap) with optional tabs ---------------- */

class LxSlider extends HTMLElement {
  connectedCallback() {
    this.prevBtn = this.querySelector('[data-lx-prev]');
    this.nextBtn = this.querySelector('[data-lx-next]');
    this.arrows = this.querySelector('.lx-arrows');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tracks = [...this.querySelectorAll('.lx-track')];
    this.frame = 0;

    const onScroll = () => this.schedule();
    this.tracks.forEach((track) => track.addEventListener('scroll', onScroll, { passive: true }));
    this.tracks.forEach((track) => this.enableDrag(track));

    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => {
        const delta = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!delta) return;
        event.preventDefault();
        const next = (index + delta + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      });
    });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.tracks.forEach((track) => this.resizeObserver.observe(track));
    }

    this.schedule();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  /* Click-and-drag scrolling for mouse users (touch already swipes natively) */
  enableDrag(track) {
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let active = false;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      active = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', (event) => {
      if (!active) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) > 6) {
        moved = true;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(event.pointerId);
      }
      if (moved) track.scrollLeft = startScroll - delta;
    });

    const end = () => {
      if (!active) return;
      active = false;
      if (!moved) return;
      track.classList.remove('is-dragging');
      // Snap to the nearest item after the drag
      const first = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const item = first ? first.getBoundingClientRect().width + gap : 0;
      if (item) track.scrollTo({ left: Math.round(track.scrollLeft / item) * item, behavior: 'smooth' });
    };

    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);
    track.addEventListener('dragstart', (event) => event.preventDefault());
  }

  get track() {
    return this.tracks.find((track) => !track.closest('[hidden]')) || this.tracks[0];
  }

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.update());
  }

  step(direction) {
    const track = this.track;
    if (!track) return;
    const first = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const item = first ? first.getBoundingClientRect().width + gap : track.clientWidth;
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / item));
    track.scrollBy({ left: direction * item * perPage * (isRTL() ? -1 : 1), behavior: 'smooth' });
  }

  update() {
    const track = this.track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const overflow = max > 4;

    if (this.arrows) this.arrows.hidden = !overflow;
    if (this.prevBtn) this.prevBtn.disabled = position <= 4;
    if (this.nextBtn) this.nextBtn.disabled = position >= max - 4;

    const bar = this.querySelector('.lx-progress');
    if (bar) {
      bar.hidden = !overflow;
      const size = Math.min(1, track.clientWidth / track.scrollWidth);
      const progress = max > 0 ? position / max : 0;
      bar.style.setProperty('--lx-progress-size', `${(size * 100).toFixed(2)}%`);
      bar.style.setProperty('--lx-progress-x', `${(progress * ((1 - size) / size) * 100).toFixed(2)}%`);
    }
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel, i) => {
      const active = i === index;
      panel.hidden = !active;
      if (active) {
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');
        panel.querySelector('.lx-track')?.scrollTo({ left: 0 });
      }
    });
    this.schedule();
  }
}

if (!customElements.get('lx-slider')) customElements.define('lx-slider', LxSlider);

/* ---------------- Product recommendations (lazy, section rendering API) ---------------- */

class LxRecs extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.querySelector('lx-slider')) return;
    if (!('IntersectionObserver' in window)) return this.load();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      if (!response.ok) throw new Error(String(response.status));
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('lx-recs');
      if (!fresh || !fresh.querySelector('lx-slider')) {
        this.closest('.shopify-section')?.setAttribute('hidden', '');
        return;
      }
      this.innerHTML = fresh.innerHTML;
      initReveal(this);
      initVideos(this);
    } catch (error) {
      this.closest('.shopify-section')?.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('lx-recs')) customElements.define('lx-recs', LxRecs);

/* ---------------- Cart drawer: reservation timer ---------------- */

const TIMER_KEY = 'lx-cart-timer-end';

class LxTimer extends HTMLElement {
  connectedCallback() {
    this.tick = this.tick.bind(this);
    this.tick();
    this.interval = setInterval(this.tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  getEnd() {
    const minutes = Number(this.dataset.minutes) || 10;
    let end = 0;
    try {
      end = Number(sessionStorage.getItem(TIMER_KEY)) || 0;
    } catch (e) {}
    if (!end || (end < Date.now() && this.dataset.restart === 'true')) {
      end = Date.now() + minutes * 60000;
      try {
        sessionStorage.setItem(TIMER_KEY, String(end));
      } catch (e) {}
    }
    return end;
  }

  tick() {
    if (!this.end) this.end = this.getEnd();
    let left = Math.max(0, this.end - Date.now());
    if (left === 0 && this.dataset.restart === 'true') {
      try { sessionStorage.removeItem(TIMER_KEY); } catch (e) {}
      this.end = this.getEnd();
      left = this.end - Date.now();
    }
    const text = this.querySelector('[data-lx-timer-text]');
    if (!text) return;
    if (left === 0) {
      text.textContent = this.dataset.expired || '';
      this.classList.add('is-expired');
      return;
    }
    const m = String(Math.floor(left / 60000)).padStart(2, '0');
    const s = String(Math.floor((left % 60000) / 1000)).padStart(2, '0');
    const time = this.querySelector('[data-lx-time]');
    if (time) time.textContent = `${m}:${s}`;
  }
}

if (!customElements.get('lx-timer')) customElements.define('lx-timer', LxTimer);

/* ---------------- Cart drawer: free shipping (multi-currency aware) ---------------- */

class LxFreeship extends HTMLElement {
  static get observedAttributes() {
    return ['data-total'];
  }

  connectedCallback() {
    this.update();
  }

  attributeChangedCallback() {
    requestAnimationFrame(() => this.update());
  }

  update() {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    if (rate === 1) return; // Liquid output is already correct in the store currency
    const threshold = Math.ceil((Number(this.dataset.threshold) * rate) / 100) * 100;
    const total = Number(this.dataset.total) || 0;
    const remaining = threshold - total;
    const text = this.querySelector('[data-lx-ship-text]');
    const bar = this.querySelector('[data-lx-ship-bar]');
    if (bar) bar.style.width = `${Math.min(100, (total / threshold) * 100).toFixed(1)}%`;
    this.classList.toggle('is-unlocked', remaining <= 0);
    if (!text) return;
    if (remaining <= 0) {
      text.textContent = this.dataset.success || '';
      return;
    }
    let amount;
    try {
      amount = new Intl.NumberFormat(doc.lang || undefined, {
        style: 'currency',
        currency: window.Shopify.currency.active,
      }).format(remaining / 100);
    } catch (e) {
      amount = (remaining / 100).toFixed(2);
    }
    text.textContent = (this.dataset.text || '').replace('[amount]', amount);
  }
}

if (!customElements.get('lx-freeship')) customElements.define('lx-freeship', LxFreeship);

/* ---------------- Play gallery videos only while visible ---------------- */

const videoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = entry.target;
            if (entry.isIntersecting) video.play?.().catch(() => {});
            else video.pause?.();
          }
        },
        { threshold: 0.25 }
      )
    : null;

function initVideos(scope = document) {
  scope.querySelectorAll('.lx-autoplay video').forEach((video) => {
    video.muted = true;
    video.playsInline = true;
    if (videoObserver) videoObserver.observe(video);
    else video.play?.().catch(() => {});
  });
}

/* ---------------- Quick add (single-variant products) ---------------- */

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-lx-add]');
  if (!button) return;
  event.preventDefault();

  const variantId = button.dataset.variantId;
  const label = button.querySelector('[data-lx-label]');
  const original = label?.textContent;
  button.classList.add('is-loading');

  try {
    const actions = window.Shopify?.actions;
    if (actions?.updateCart) {
      const result = await actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'product' } }
      );
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
    } else {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error('add failed');
    }

    button.classList.remove('is-loading');
    button.classList.add('is-added');
    if (label && button.dataset.addedText) label.textContent = button.dataset.addedText;

    if (window.Shopify?.actions?.openCart) {
      window.Shopify.actions.openCart();
    } else {
      window.location.href = window.Theme?.routes?.cart_url || '/cart';
    }

    setTimeout(() => {
      button.classList.remove('is-added');
      if (label && original) label.textContent = original;
    }, 2200);
  } catch (error) {
    button.classList.remove('is-loading');
    // Fall back to the product page so the shopper can still buy
    const href = button.dataset.fallback;
    if (href) window.location.href = href;
  }
});

/* ---------------- Header scroll state ---------------- */

let ticking = false;
const setScrolled = () => {
  doc.classList.toggle('lx-scrolled', window.scrollY > 8);
  ticking = false;
};
window.addEventListener(
  'scroll',
  () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(setScrolled);
  },
  { passive: true }
);
setScrolled();

/* ---------------- Boot + theme editor support ---------------- */

initReveal();
initVideos();

document.addEventListener('shopify:section:load', (event) => {
  initReveal(event.target);
  initVideos(event.target);
});
document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest('.lx-track > *');
  slide?.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
  event.target.closest('[data-lx-reveal]')?.classList.add('is-visible');
});

/* ---------------- Beräknad leverans (arbetsdagar, svenska månader) ---------------- */

const SV_MONTHS = ['jan', 'feb', 'mars', 'apr', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'];

function addBusinessDays(start, days) {
  const date = new Date(start);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date;
}

function initDelivery(scope = document) {
  scope.querySelectorAll('[data-lx-delivery]').forEach((el) => {
    const min = Number(el.dataset.min || 7);
    const max = Math.max(min, Number(el.dataset.max || 14));
    const cutoff = Number(el.dataset.cutoff || 14);
    const now = new Date();
    let start = new Date(now);
    if (now.getHours() >= cutoff) start.setDate(start.getDate() + 1);
    while (start.getDay() === 0 || start.getDay() === 6) start.setDate(start.getDate() + 1);
    start.setDate(start.getDate() - 1);
    const from = addBusinessDays(start, min);
    const to = addBusinessDays(start, max);
    const fromText =
      from.getMonth() === to.getMonth() ? String(from.getDate()) : `${from.getDate()} ${SV_MONTHS[from.getMonth()]}`;
    el.textContent = `${fromText}–${to.getDate()} ${SV_MONTHS[to.getMonth()]}`;
  });
}

/* ---------------- Senast visade produkter ---------------- */

const RV_KEY = 'lx-recently-viewed';

function readViewed() {
  try {
    return JSON.parse(localStorage.getItem(RV_KEY) || '[]');
  } catch (error) {
    return [];
  }
}

class LxRecentlyViewed extends HTMLElement {
  connectedCallback() {
    const current = this.dataset.current;
    const limit = Number(this.dataset.limit || 8);
    const handles = readViewed().filter((handle) => handle && handle !== current).slice(0, limit);
    if (current) {
      try {
        const next = [current, ...readViewed().filter((handle) => handle !== current)].slice(0, 20);
        localStorage.setItem(RV_KEY, JSON.stringify(next));
      } catch (error) {
        /* lagring blockerad – sektionen visas helt enkelt inte */
      }
    }
    if (!handles.length) return;
    this.load(handles);
  }

  async load(handles) {
    const list = this.querySelector('[data-lx-rv-list]');
    const sectionId = this.dataset.sectionId;
    const root = window.Shopify?.routes?.root || '/';
    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${sectionId}`);
          if (!response.ok) return '';
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          return html.querySelector('template[data-lx-rv-card]')?.innerHTML || '';
        } catch (error) {
          return '';
        }
      })
    );
    const cards = results.filter(Boolean);
    if (!cards.length || !list) return;
    list.innerHTML = cards.map((card) => `<li>${card}</li>`).join('');
    this.hidden = false;
  }
}

if (!customElements.get('lx-recently-viewed')) customElements.define('lx-recently-viewed', LxRecentlyViewed);

initDelivery();
document.addEventListener('shopify:section:load', (event) => initDelivery(event.target));
document.addEventListener('variant:update', () => setTimeout(() => initDelivery(), 50));
